<template>
  <PageWrap page-title="Creación de agenda de visitas">
    <b-row>
      <b-col cols="6">
        <BfInput
          v-model="searchValue"
          :suffix-icon="!searchingChargesPendingSchedule ? 'el-icon-search' : 'el-icon-loading'"
          label="Búsqueda"
          placeholder="Busca por código, cliente o población"
          data-key="search-all"
          @update="updateSearch"
        />
      </b-col>
    </b-row>

    <LoadingIndicator v-if="loadingData" />

    <template v-else>
      <b-row class="mb-5">
        <b-col cols="8">
          <BfTitle size="small" class="mb-3">Mis encargos</BfTitle>

          <ChargesTable
            :data="chargesPendingSchedule"
            :selected-rows="selectedChargesPending"
            allow-select
            max-height="500"
            @row-checkbox-change="chargesPendingRowCheckbox"
          />

          <ScheduleVisitForm
            v-if="pendingScheduleChargeSelected && scheduleForm"
            :data="scheduleVisitFormObject"
            class="mt-4 align-items-end"
            @update="updateScheduleVisitForm"
          >
            <b-col>
              <BfButton
                variant="primary"
                :disabled="disableScheduleFormSubmit"
                @click="scheduleVisit"
              >
                Agendar
              </BfButton>
            </b-col>
            <b-col class="text-right">
              <BfButton
                variant="red"
                @click="unassignCharge"
              >
                Desasignar
              </BfButton>
            </b-col>
          </ScheduleVisitForm>
        </b-col>

        <b-col cols="4">
          <ChargeSidePreview
            v-if="pendingScheduleChargeSelected"
            :charge-data="sidePreviewData"
          />
        </b-col>
      </b-row>

      <b-row>
        <b-col cols="12">
          <BfTitle size="small" class="mb-3">Mis visitas</BfTitle>

          <VisitTable
            :data="scheduledVisitsData"
            :selectedRows="selectedScheduledVisits"
            allow-select
            max-height="500"
            @row-checkbox-change="scheduledVisitsRowCheckbox"
          />

          <div v-if="scheduledVisitSelected" class="mt-4">
            <BfButton
              variant="red"
              @click="unscheduleVisit"
            >
              Eliminar
            </BfButton>
          </div>
        </b-col>
      </b-row>
    </template>
  </PageWrap>
</template>

<script>
import { mapState } from 'vuex';
import PageWrap from '@/components/PageWrap';
import ChargeSidePreview from '@/components/ChargeSidePreview';
import LoadingIndicator from '@/components/LoadingIndicator';
import ChargesTable from '@/components/Tables/ChargesTable';
import VisitTable from '@/components/Tables/VisitTable';
import ScheduleVisitForm from '@/components/FormGroups/ScheduleVisitForm';
import { chargeAPI, visitAPI } from '@/api';
import { get, debounce, moment } from '@/utils';

const DURATION_DEFAULT_VALUE = 1;

export default {
  name: 'ScheduleAssessorVisits',

  data: () => ({
    chargesPendingSchedule: [],
    scheduledVisitsData: [],
    loadingData: false,
    searchValue: '',
    searchingChargesPendingSchedule: false,
    scheduleDateValue: '',
    scheduleDurationValue: DURATION_DEFAULT_VALUE,
    scheduleForm: {},
    selectedChargesPending: [],
    selectedScheduledVisits: [],
    tasadorId: null,
  }),

  async created() {
    this.loadingData = true;
    try {
      await this.loadData();
    } finally {
      this.loadingData = false;
    }
  },

  methods: {
    async loadData() {
      await Promise.all([
        this.currentUserTasadorId(),
        this.loadPendingCharges(),
        this.loadScheduledVisits(),
      ]);
    },

    async loadPendingCharges() {
      const {
        data: { data },
      } = await chargeAPI.getAllForVisitStatusWithoutPending({
        assessorId: this.tasadorId,
        perPage: 99,
        include: 'solicitante,sucursal',
        search: this.searchValue,
      });

      this.chargesPendingSchedule = data.map(({ solicitante = {}, sucursal = {}, ...charge }) => ({
        ...charge,
        solicitante_nombre: get(solicitante, ['nombre'], ''),
        sucursal_nombre: get(sucursal, ['nombre'], ''),
      }));
    },

    async loadScheduledVisits() {
      const { data } = await visitAPI.getAllByAssessor(this.tasadorId);
      this.scheduledVisitsData = data.data;
    },

    /* eslint-disable-next-line func-names */
    updateSearch: debounce(async function() {
      this.searchingChargesPendingSchedule = true;
      try {
        await this.loadPendingCharges();
      } finally {
        this.searchingChargesPendingSchedule = true;
      }
    }, 350),

    scheduledVisitsRowCheckbox({ row }) {
      const { id } = row;
      const currentIndex = this.selectedScheduledVisits.indexOf(id);

      if (currentIndex >= 0) {
        this.$delete(this.selectedScheduledVisits, currentIndex);
      } else {
        this.selectedScheduledVisits = [...this.selectedScheduledVisits, id];
      }
    },

    async chargesPendingRowCheckbox({ value, row }) {
      this.selectedChargesPending = value ? [row.id] : [];

      if (value) {
        const { data } = await visitAPI.preCreate(row.id);
        this.scheduleForm = data;
      } else {
        this.scheduleForm = {};
      }
    },

    async scheduleVisit() {
      if (this.pendingScheduleChargeSelected && !this.disableScheduleFormSubmit) {
        const [chargeId] = this.selectedChargesPending;

        try {
          const { message = '' } = await visitAPI.create({
            chargeId,
            data: {
              ...this.scheduleForm,
              fecha_hora_fin: this.scheduleEndDate,
              tasadores_id: this.tasadorId,
            },
          });

          this.scheduleDurationValue = DURATION_DEFAULT_VALUE;
          this.scheduleForm.fecha_hora_ini = null;
          this.selectedChargesPending = [];

          this.loadData();

          this.$bfNotify({
            type: 'success',
            title: 'Guardado',
            message,
          });
        } catch (err) {
          // console.log('CREATE_SCHEDULE_ENTRY_ERROR', err);
        }
      }
    },

    async unscheduleVisit() {
      if (this.scheduledVisitSelected) {
        /* eslint-disable-next-line arrow-parens */
        const requests = this.selectedScheduledVisits.map(async visitId => {
          const visit = this.scheduledVisitsData.find(c => c.id === visitId);
          const { encargos_id: chargeId = null } = visit || {};

          if (visitId && chargeId) {
            const { message } = await visitAPI.unschedule(visitId);

            this.$bfNotify({
              type: 'success',
              title: 'Desagendado',
              message,
            });
          }
        });

        await Promise.all(requests);

        this.selectedScheduledVisits = [];

        this.loadData();
      }
    },

    async unassignCharge() {
      if (this.pendingScheduleChargeSelected) {
        const [chargeId] = this.selectedChargesPending;
        const { message = '' } = await chargeAPI.update({
          id: chargeId,
          data: {
            tasador_encargado_id: null,
            estado: 'B',
          },
        });

        this.selectedChargesPending = [];
        this.loadData();

        this.$bfNotify({
          type: 'success',
          title: 'Desasignado',
          message,
        });
      }
    },

    currentUserTasadorId() {
      this.tasadorId = this.user.tasador_id;
    },

    updateScheduleVisitForm({ key, value }) {
      if (key === 'duration') {
        this.scheduleDurationValue = value;
      } else {
        this.scheduleForm = {
          ...this.scheduleForm,
          [key]: value,
        };
      }
    },
  },

  computed: {
    ...mapState({ user: ({ auth }) => auth.user }),

    pendingScheduleChargeSelected() {
      return !!this.selectedChargesPending.length;
    },

    scheduledVisitSelected() {
      return !!this.selectedScheduledVisits.length;
    },

    scheduleEndDate() {
      if (this.scheduleForm.fecha_hora_ini) {
        const endDate = moment(this.scheduleForm.fecha_hora_ini)
          .add('hours', this.scheduleDurationValue)
          .format('YYYY-MM-DD HH:mm:ss');

        return endDate;
      }

      return '';
    },

    scheduleVisitFormObject() {
      return {
        ...this.scheduleForm,
        duration: this.scheduleDurationValue,
      };
    },

    disableScheduleFormSubmit() {
      return this.scheduleDurationValue <= 0 || !this.scheduleForm.fecha_hora_ini;
    },

    sidePreviewData() {
      if (!this.pendingScheduleChargeSelected) {
        return {};
      }

      return this.chargesPendingSchedule.find(({ id }) => id === this.selectedChargesPending[0]);
    },
  },

  components: {
    ChargeSidePreview,
    ChargesTable,
    VisitTable,
    LoadingIndicator,
    PageWrap,
    ScheduleVisitForm,
  },
};
</script>
